// @ts-check
/* ═══════════════════════ PRE-FLIGHT NEDTELLING (#6) ═══════════════════════
   Tidssatt «gjør dette nå»-liste. CHKLST har allerede fasegrupper
   («Nå – ved aksept», «God tid før», «14 dager før», «24 timer før»).
   Her knytter vi hver gruppe til en konkret frist relativt til avreise
   (17. juli) og løfter fram det som haster nå — sortert på frist, med
   uavkryssede punkter først. Slik fanger vi ESTA/eTA, pass-utløp,
   forsikring og førerkort før det blir for sent.
   ═══════════════════════════════════════════════════════════════════════ */

import { CHKLST } from './data.js';
import { S } from './state.js';
import { esc } from './utils.js';

const DEPARTURE = new Date('2026-07-17T13:50:00+02:00');

// Frist (antall dager FØR avreise) per CHKLST-gruppe, matchet på rekkefølge.
// Gruppe 0 = ved aksept (bør være gjort for lengst), 3 = 24 t før.
const GROUP_LEAD_DAYS = [30, 21, 14, 1];

function daysUntil(date){
  return Math.ceil((date - new Date()) / 86400000);
}

function groupDeadline(leadDays){
  const d = new Date(DEPARTURE);
  d.setDate(d.getDate() - leadDays);
  d.setHours(23, 59, 59, 0);
  return d;
}

function groupDone(group){
  return group.items.every(it => S.chk[it.id]);
}

function groupOpenItems(group){
  return group.items.filter(it => !S.chk[it.id]);
}

/**
 * Returnerer en sortert liste over åpne checklist-grupper med fristinfo.
 * Mest presserende først (minst dager til frist).
 */
export function preflightStatus(){
  const out = [];
  CHKLST.forEach((group, i) => {
    if (groupDone(group)) return;
    const lead = GROUP_LEAD_DAYS[i] ?? 7;
    const deadline = groupDeadline(lead);
    const daysLeft = daysUntil(deadline);
    out.push({
      group, daysLeft,
      open: groupOpenItems(group),
      overdue: daysLeft < 0,
      urgent: daysLeft >= 0 && daysLeft <= 3,
    });
  });
  out.sort((a, b) => a.daysLeft - b.daysLeft);
  return out;
}

/** Det aller mest presserende åpne punktet — brukes i forside-briefen. */
export function topPreflightAction(){
  const st = preflightStatus();
  if (!st.length) return null;
  const first = st[0];
  return {
    text: first.open[0]?.t || first.group.g,
    daysLeft: first.daysLeft,
    overdue: first.overdue,
  };
}

function urgencyChip(b){
  if (b.overdue) return `<span class="pf-chip pf-over">${Math.abs(b.daysLeft)} d på overtid</span>`;
  if (b.daysLeft === 0) return `<span class="pf-chip pf-urg">frist i dag</span>`;
  if (b.urgent) return `<span class="pf-chip pf-urg">frist om ${b.daysLeft} d</span>`;
  return `<span class="pf-chip pf-ok">frist om ${b.daysLeft} d</span>`;
}

/** Render et kompakt nedtellingskort som prependes til sjekklisten. */
export function renderPreflight(){
  const days = daysUntil(DEPARTURE);
  if (days < 0) return ''; // reisen er i gang/over — skjul nedtelling
  const st = preflightStatus();
  const openCount = st.reduce((s, b) => s + b.open.length, 0);

  const header = `
    <div class="pf-countdown">
      <div class="pf-cd-num">${days}</div>
      <div class="pf-cd-lbl">dager til avreise<br><span>17. juli · 13:50 OSL</span></div>
    </div>`;

  if (!st.length) {
    return `<div class="pf-card pf-card-done">
      ${header}
      <div class="pf-allclear">✓ Alt på sjekklisten er i orden. God reise!</div>
    </div>`;
  }

  // Vis de tre mest presserende gruppene med deres åpne punkter.
  const blocks = st.slice(0, 3).map(b => `
    <div class="pf-block${b.overdue ? ' pf-block-over' : b.urgent ? ' pf-block-urg' : ''}">
      <div class="pf-block-hdr">
        <span class="pf-block-title">${esc(b.group.g)}</span>
        ${urgencyChip(b)}
      </div>
      <ul class="pf-items">
        ${b.open.slice(0, 4).map(it =>
          `<li onclick="toggleChk('${it.id}')"><span class="pf-tick">○</span>${esc(it.t)}</li>`
        ).join('')}
        ${b.open.length > 4 ? `<li class="pf-more">+ ${b.open.length - 4} til</li>` : ''}
      </ul>
    </div>`).join('');

  return `<div class="pf-card">
    ${header}
    <div class="pf-lead">${openCount} ${openCount === 1 ? 'punkt gjenstår' : 'punkter gjenstår'} — gjør det som haster nå:</div>
    ${blocks}
  </div>`;
}
